import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Send,
  Users,
  Filter,
  CheckSquare,
  Square,
  Mail,
  Loader2,
  Clock,
  AlertTriangle,
  X,
  Zap,
  Sparkles,
  History,
  TrendingUp,
  Target,
} from "lucide-react";
import { API, STAGES, TIERS, SECTORS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BespokeCampaignWizard from "@/components/BespokeCampaignWizard";
import TargetListsPanel from "@/components/TargetListsPanel";
import BespokeCampaignHistory from "@/components/BespokeCampaignHistory";
import BespokeCampaignAnalytics from "@/components/BespokeCampaignAnalytics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

export default function Campaigns({ user }) {
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["quick", "bespoke", "history", "insights"].includes(t) ? t : "quick";
  });
  const [resumeId, setResumeId] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [filterStage, setFilterStage] = useState("all");
  const [filterTier, setFilterTier] = useState("all");
  const [filterSector, setFilterSector] = useState("all");
  
  // Confirmation dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    fetchData();
    // Refresh scheduled emails every 30 seconds
    const interval = setInterval(fetchScheduledEmails, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, templatesRes] = await Promise.all([
        fetch(`${API}/clients`, { credentials: "include" }),
        fetch(`${API}/templates`, { credentials: "include" }),
      ]);

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data);
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        // Dedupe by template_id (backend can return duplicates) to avoid
        // React "two children with the same key" warnings.
        const seen = new Set();
        setTemplates((data || []).filter((t) => {
          if (seen.has(t.template_id)) return false;
          seen.add(t.template_id);
          return true;
        }));
      }
      
      await fetchScheduledEmails();
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledEmails = async () => {
    try {
      const response = await fetch(`${API}/email/scheduled`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setScheduledEmails(data);
      }
    } catch (error) {
      console.error("Error fetching scheduled emails:", error);
    }
  };

  const filteredClients = clients.filter((client) => {
    const hasEmail = client.contacts?.some((c) => c.email);
    if (!hasEmail) return false;

    const matchesStage = filterStage === "all" || client.stage === filterStage;
    const matchesTier = filterTier === "all" || client.tier === filterTier;
    const matchesSector = filterSector === "all" || client.sector?.includes(filterSector);

    return matchesStage && matchesTier && matchesSector;
  });

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map((c) => c.client_id));
    }
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Step 1: Initial send button click
  const handleSendClick = () => {
    if (selectedClients.length === 0) {
      toast.error("Please select at least one client");
      return;
    }
    if (!selectedTemplate) {
      toast.error("Please select an email template");
      return;
    }
    setConfirmDialogOpen(true);
  };

  // Step 2: User confirms they want to send
  const handleConfirmSend = () => {
    setConfirmDialogOpen(false);
    setScheduleDialogOpen(true);
  };

  // Step 3a: Schedule for 15 minutes
  const handleScheduleEmail = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API}/email/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_ids: selectedClients,
          template_id: selectedTemplate,
          personalise: true,
          send_immediately: false,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setSelectedClients([]);
        setScheduleDialogOpen(false);
        fetchScheduledEmails();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to schedule email");
      }
    } catch (error) {
      toast.error("Failed to schedule email");
    } finally {
      setSending(false);
    }
  };

  // Step 3b: Override and send immediately
  const handleSendNow = async () => {
    setSending(true);
    try {
      const response = await fetch(`${API}/email/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_ids: selectedClients,
          template_id: selectedTemplate,
          personalise: true,
          send_immediately: true,
        }),
      });

      if (response.ok) {
        // Immediately process the scheduled email
        await fetch(`${API}/email/process-scheduled`, {
          method: "POST",
          credentials: "include",
        });
        
        toast.success(`Emails sent to ${selectedClients.length} client(s)!`);
        setSelectedClients([]);
        setScheduleDialogOpen(false);
        fetchScheduledEmails();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  // Cancel a scheduled email
  const handleCancelScheduled = async (scheduleId) => {
    setCancelling(scheduleId);
    try {
      const response = await fetch(`${API}/email/scheduled/${scheduleId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Scheduled email cancelled");
        fetchScheduledEmails();
      } else {
        toast.error("Failed to cancel scheduled email");
      }
    } catch (error) {
      toast.error("Failed to cancel scheduled email");
    } finally {
      setCancelling(null);
    }
  };

  // Calculate time remaining for scheduled emails
  const getTimeRemaining = (scheduledFor) => {
    const now = new Date();
    const scheduled = new Date(scheduledFor);
    const diff = scheduled - now;
    
    if (diff <= 0) return "Sending now...";
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const selectedTemplateName = templates.find(t => t.template_id === selectedTemplate)?.name || '';

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
          Email Campaigns
        </h1>
        <p className="text-slate-500 mt-1">
          Quick template sends, or bespoke per-client outreach with Hunter.io
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="campaigns-tabs">
          <TabsTrigger value="quick" data-testid="tab-quick"><Mail className="w-4 h-4 mr-2" />Quick send</TabsTrigger>
          <TabsTrigger value="bespoke" data-testid="tab-bespoke"><Sparkles className="w-4 h-4 mr-2" />Bespoke outreach</TabsTrigger>
          <TabsTrigger value="targets" data-testid="tab-targets"><Target className="w-4 h-4 mr-2" />Target lists</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-2" />Campaigns</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights"><TrendingUp className="w-4 h-4 mr-2" />Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="bespoke" className="mt-6">
          <BespokeCampaignWizard resumeId={resumeId} onResumed={() => setResumeId(null)} />
        </TabsContent>

        <TabsContent value="targets" className="mt-6">
          <TargetListsPanel />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <BespokeCampaignHistory onResume={(id) => { setResumeId(id); setActiveTab("bespoke"); }} />
        </TabsContent>

        <TabsContent value="insights" className="mt-6">
          <BespokeCampaignAnalytics />
        </TabsContent>

        <TabsContent value="quick" className="mt-6 space-y-6">
      {/* Scheduled Emails Panel */}
      {scheduledEmails.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              Scheduled Emails ({scheduledEmails.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {scheduledEmails.map((sched) => (
                <div
                  key={sched.schedule_id}
                  className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-amber-600">
                        {getTimeRemaining(sched.scheduled_for)}
                      </div>
                      <div className="text-xs text-slate-500">remaining</div>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {sched.template_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        To: {sched.client_count} client(s) • By: {sched.created_by_name}
                      </p>
                      <p className="text-xs text-slate-400">
                        {sched.client_names?.slice(0, 3).join(", ")}
                        {sched.client_count > 3 && ` +${sched.client_count - 3} more`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleCancelScheduled(sched.schedule_id)}
                    disabled={cancelling === sched.schedule_id}
                    data-testid={`cancel-${sched.schedule_id}`}
                  >
                    {cancelling === sched.schedule_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Filters and Selection */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Select Recipients
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Stage</Label>
                  <Select value={filterStage} onValueChange={setFilterStage}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Stages" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stages</SelectItem>
                      {STAGES.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tier</Label>
                  <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Tiers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tiers</SelectItem>
                      {TIERS.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sector</Label>
                  <Select value={filterSector} onValueChange={setFilterSector}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Sectors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sectors</SelectItem>
                      {SECTORS.map((sector) => (
                        <SelectItem key={sector} value={sector}>
                          {sector}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Client Selection */}
              <div className="border rounded-lg max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={
                            selectedClients.length === filteredClients.length &&
                            filteredClients.length > 0
                          }
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Stage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((client) => {
                      const contact = client.contacts?.[0] || {};
                      return (
                        <TableRow key={client.client_id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedClients.includes(client.client_id)}
                              onCheckedChange={() =>
                                handleSelectClient(client.client_id)
                              }
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {client.company_name}
                          </TableCell>
                          <TableCell>{contact.name || "-"}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {contact.email || "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{client.stage}</Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredClients.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-slate-500 py-8"
                        >
                          No clients with email addresses found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {selectedClients.length} of {filteredClients.length} selected
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedClients([])}
                >
                  Clear Selection
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Template Selection and Send */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Template
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select
                value={selectedTemplate}
                onValueChange={setSelectedTemplate}
              >
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem
                      key={template.template_id}
                      value={template.template_id}
                    >
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedTemplate && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <p className="font-medium mb-1">
                    Subject:{" "}
                    {templates.find((t) => t.template_id === selectedTemplate)
                      ?.subject || ""}
                  </p>
                  <p className="text-slate-500 text-xs">
                    Variables like {"{{contact_name}}"} will be personalised
                  </p>
                </div>
              )}

              <div className="pt-4">
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={handleSendClick}
                  disabled={selectedClients.length === 0 || !selectedTemplate}
                  data-testid="send-campaign-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Campaign ({selectedClients.length})
                </Button>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Emails will be scheduled with a 15-minute safety delay
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Step 1: Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Email Campaign
            </DialogTitle>
            <DialogDescription>
              You are about to send an email campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <p className="text-sm">
                <span className="font-medium">Recipients:</span>{" "}
                <span className="text-blue-600 font-bold">{selectedClients.length} client(s)</span>
              </p>
              <p className="text-sm">
                <span className="font-medium">Template:</span>{" "}
                {selectedTemplateName}
              </p>
            </div>
            <p className="text-sm text-slate-600">
              Are you sure you want to send this email to{" "}
              <strong>{selectedClients.length}</strong> real client(s)?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSend}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-send-btn"
            >
              Yes, Schedule Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2: Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Email Scheduled
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-10 h-10 text-blue-600" />
            </div>
            <p className="text-lg text-slate-900 mb-2">
              This email will go out in{" "}
              <span className="font-bold text-blue-600">15 minutes</span>
            </p>
            <p className="text-sm text-slate-500">
              To {selectedClients.length} recipient(s). You can cancel it anytime before it sends.
            </p>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setScheduleDialogOpen(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleEmail}
              disabled={sending}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="schedule-ok-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Clock className="w-4 h-4 mr-2" />
              )}
              OK, Schedule
            </Button>
            <Button
              onClick={handleSendNow}
              disabled={sending}
              variant="destructive"
              data-testid="send-now-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Send Now (Override)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
